export const SUPPORTED_FILTER_KEYS = [
  'expert_type',
  'services_offered',
  'countries',
  'cities',
  'languages',
  'apps_and_themes',
  'partner_tiers',
  'partner_badge',
  'price_from',
  'price_to',
  'search',
  'is_pro',
  'ranking',
] as const;

export const LIST_FILTER_KEYS = new Set([
  'services_offered',
  'countries',
  'cities',
  'languages',
  'apps_and_themes',
  'partner_tiers',
]);
export const EXPERT_TYPES = new Set(['freelancer', 'agency', 'app_service_provider']);
export const PARTNER_TIERS = new Set(['select', 'plus', 'premier', 'platinum']);

export interface SuperCollectionPresetResult {
  normalized: string;
  errors: string[];
}

export function queryParamsFromInput(input: string): { params: URLSearchParams; errors: string[] } {
  const raw = input.trim();
  if (!raw) return { params: new URLSearchParams(), errors: ['Paste a filtered /experts/all URL or enter its query string.'] };

  try {
    const hasPath = /^https?:\/\//i.test(raw) || raw.startsWith('/');
    const url = new URL(hasPath ? raw : `?${raw.replace(/^\?/, '')}`, 'https://shopexperts.com/experts/all');
    const errors = hasPath && url.pathname.replace(/\/$/, '') !== '/experts/all'
      ? ['The copied URL must point to /experts/all.']
      : [];
    return { params: url.searchParams, errors };
  } catch {
    return { params: new URLSearchParams(), errors: ['The preset is not a valid URL or query string.'] };
  }
}

export function normalizeSuperCollectionPreset(input: unknown): SuperCollectionPresetResult {
  if (typeof input !== 'string') {
    return { normalized: '', errors: ['Preset filters must be text.'] };
  }

  const { params, errors } = queryParamsFromInput(input);
  const supported = new URLSearchParams();
  const unknownKeys = [...new Set([...params.keys()].filter(key => !SUPPORTED_FILTER_KEYS.includes(key as typeof SUPPORTED_FILTER_KEYS[number])))];
  if (unknownKeys.length) errors.push(`Unsupported filter${unknownKeys.length > 1 ? 's' : ''}: ${unknownKeys.join(', ')}.`);

  for (const key of SUPPORTED_FILTER_KEYS) {
    const values = params.getAll(key);
    if (!values.length) continue;
    if (values.length > 1) {
      errors.push(`${key} must appear once; use comma-separated values where applicable.`);
      continue;
    }

    const rawValue = values[0]?.trim() || '';
    if (!rawValue) {
      errors.push(`${key} cannot be empty.`);
      continue;
    }

    if (key === 'expert_type') {
      if (!EXPERT_TYPES.has(rawValue)) errors.push('expert_type must be freelancer, agency, or app_service_provider.');
      else supported.set(key, rawValue);
      continue;
    }

    if (LIST_FILTER_KEYS.has(key)) {
      const entries = [...new Set(rawValue.split(',').map(value => value.trim()).filter(Boolean))];
      if (!entries.length || entries.some(value => !/^[a-z0-9._~-]+$/i.test(value))) {
        errors.push(`${key} must contain comma-separated slugs or codes.`);
      } else if (key === 'partner_tiers' && entries.some(value => !PARTNER_TIERS.has(value))) {
        errors.push('partner_tiers may only contain select, plus, premier, or platinum.');
      } else if (key === 'cities' && entries.some(value => !/^\d+$/.test(value))) {
        errors.push('cities must contain comma-separated numeric city ids.');
      } else {
        supported.set(key, entries.join(','));
      }
      continue;
    }

    if (key === 'price_from' || key === 'price_to') {
      const price = Number(rawValue);
      if (!/^\d+$/.test(rawValue) || !Number.isSafeInteger(price)) {
        errors.push(`${key} must be a non-negative safe whole number.`);
      } else {
        supported.set(key, String(price));
      }
      continue;
    }

    if (key === 'is_pro') {
      if (rawValue !== 'true') errors.push('is_pro may only be set to true.');
      else supported.set(key, rawValue);
      continue;
    }

    if (key === 'ranking') {
      if (rawValue !== 'most_recent') errors.push('ranking may only be most_recent.');
      else supported.set(key, rawValue);
      continue;
    }

    supported.set(key, rawValue);
  }

  const priceFrom = supported.get('price_from');
  const priceTo = supported.get('price_to');
  if (priceFrom && priceTo && Number(priceFrom) > Number(priceTo)) {
    errors.push('price_from cannot be greater than price_to.');
  }
  if (![...supported.keys()].length) errors.push('Choose at least one supported expert filter.');

  return { normalized: supported.toString(), errors };
}

export function validateSuperCollectionPath(path: string): string {
  const normalized = path.trim().replace(/\/$/, '');
  if (!/^\/collections\/[a-z0-9]+(?:-[a-z0-9]+)*$/i.test(normalized)) {
    return 'Super-collection paths must use /collections/your-collection-slug.';
  }
  return '';
}
